import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash, CaretUp, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

export default function AdminHubs() {
  const [hubs, setHubs] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/hubs");
    setHubs(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/hubs", { name });
      setName("");
      toast.success("Hub added");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const move = async (id, direction) => {
    try {
      const { data } = await api.post(`/hubs/${id}/move`, { direction });
      setHubs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this hub?")) return;
    try {
      await api.delete(`/hubs/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900">Hubs</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Add hub names and set their display order with the up / down arrows.
        </p>
      </div>

      <form onSubmit={add} className="mt-6 flex items-center gap-2 max-w-md">
        <Input
          data-testid="new-hub-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Howrah Hub"
          required
        />
        <Button type="submit" disabled={saving || !name.trim()} data-testid="add-hub-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={16} className="mr-1.5" /> Add hub
        </Button>
      </form>

      <div className="mt-4 relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          data-testid="hub-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search hub name…"
          className="pl-9"
        />
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-lg overflow-hidden max-w-2xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-2.5 font-medium w-12">#</th>
              <th className="px-4 py-2.5 font-medium">Hub name</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hubs.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-zinc-500">No hubs yet. Add the first one above.</td></tr>
            )}
            {hubs
              .filter((h) => !search.trim() || h.name.toLowerCase().includes(search.trim().toLowerCase()))
              .map((h, i, arr) => (
              <tr key={h.id} data-testid={`hub-row-${h.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{i + 1}</td>
                <td className="px-4 py-3 text-zinc-900">{h.name}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button variant="ghost" size="sm" disabled={i === 0 || !!search.trim()}
                      data-testid={`hub-up-${h.id}`}
                      onClick={() => move(h.id, "up")}
                      className="text-zinc-600 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-30">
                      <CaretUp size={15} weight="bold" />
                    </Button>
                    <Button variant="ghost" size="sm" disabled={i === arr.length - 1 || !!search.trim()}
                      data-testid={`hub-down-${h.id}`}
                      onClick={() => move(h.id, "down")}
                      className="text-zinc-600 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-30">
                      <CaretDown size={15} weight="bold" />
                    </Button>
                    <Button variant="ghost" size="sm"
                      data-testid={`delete-hub-${h.id}`}
                      onClick={() => remove(h.id)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
